/**
 * CompletenessPreview · iter303ak-fix7 — Quote Sent · Task 1 · Action 6
 * ────────────────────────────────────────────────────────────────────
 * Final gate for the Site Appraisal task. Reads all the data captured
 * across Actions 1-5 and shows a green-tick summary. Blocks Continue
 * until every core item is satisfied. Each red-X row provides a
 * "Jump to Action N →" button that navigates directly to the failing
 * action (iter303ak-fix7); the operator can fix that one thing and use
 * the sticky "↩ Return to Preview" pill to hop straight back here.
 *
 * ROOF APPRAISAL DATA — iter303ak-fix7 corrected field names:
 *   roof_condition, ridge_condition, tile_condition, valley_condition,
 *   barge_condition, sheet_condition, flashing_condition,
 *   spouting_condition, fastening_condition, appraisal_condition
 * (Previously used sheeting_condition, flashings_condition,
 * guttering_condition, downpipes_condition — none of which exist.)
 *
 * URGENT CONCERNS — iter303ak-fix7 corrected read location: the concerns
 * live on the primary roof_details row (roof_details[0].urgent_concerns)
 * — the field on property is a legacy fallback we still check second.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, RefreshCw, AlertCircle, ClipboardCheck, ArrowRight } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const ROOF_FIELDS = ['roof_type', 'material_type', 'roof_brand', 'roof_colour', 'roof_pitch', 'roof_stories', 'roof_m2', 'ridge_mtr'];

const AREA_CONDITIONS = [
  'roof_condition', 'ridge_condition', 'tile_condition', 'valley_condition',
  'barge_condition', 'sheet_condition', 'flashing_condition',
  'spouting_condition', 'fastening_condition', 'appraisal_condition',
];

// Map completeness rows → the source Action order (within Task 1) so the
// Jump button knows where to hop. Physical+Report path has 6 actions:
//   1 · Roof Snapshot + Template Confirm
//   2 · Refresh Roof Details
//   3 · Enter Appraisal Data
//   4 · Annotate Roof Diagram
//   5 · Refresh Urgent Concerns
//   6 · Completeness Preview (this action)
const ROW_TO_ACTION_ORDER = {
  screenshot: 10,
  template: 10,
  roof_detail: 20,
  mode: 30,
  data: 30,
  pins: 40,
  concerns: 50,
};

export default function CompletenessPreview({ action, capturedData, onNext, onBack, onCancel, canBack, onJumpTo, actionFlat }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const propertyId = capturedData?.property_id;

  const [appraisal, setAppraisal] = useState(null);
  const [property, setProperty] = useState(null);
  const [roofDetails, setRoofDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [];
      if (appraisalId) promises.push(api.get(`/appraisals/${appraisalId}`).then(r => setAppraisal(r.data)).catch(() => {}));
      if (propertyId) promises.push(api.get(`/properties/${propertyId}`).then(r => setProperty(r.data)).catch(() => {}));
      if (propertyId) promises.push(
        api.get(`/roof-details/${propertyId}`).then(r => {
          const rows = Array.isArray(r.data) ? r.data : [];
          const primary = rows.find(x => (x.description || '').toLowerCase() === 'main roof') || rows[0] || {};
          setRoofDetails(primary);
        }).catch(() => {})
      );
      await Promise.all(promises);
    } finally { setLoading(false); }
  }, [appraisalId, propertyId]);

  useEffect(() => { load(); }, [load]);

  // Resolve an ordered action within the current task by its order number.
  // The Runner shell passes actionFlat = run._flat_actions which is
  // already scoped to the current task, so a simple `order` match is
  // enough (no task_id disambiguation needed).
  const jumpTo = (order) => {
    if (!onJumpTo || !Array.isArray(actionFlat)) return;
    const target = actionFlat.find(e => (e?.action?.order ?? -1) === order);
    if (target?.action?.id) {
      onJumpTo(target.action.id, { divertedFrom: action.id });
    }
  };

  // Aggregate the completeness checks.
  const checks = (() => {
    const hasScreenshot = !!(property?.roof_screenshot_photo_id || property?.roof_diagram_photo_id);
    const hasTemplate = !!property?.roof_template_id;
    const roofDataSrc = { ...(property || {}), ...(roofDetails || {}) };
    const roofFilledCount = ROOF_FIELDS.filter(k => {
      const v = roofDataSrc[k];
      return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
    }).length;
    const roofPct = Math.round((roofFilledCount / ROOF_FIELDS.length) * 100);
    const reportVariant = appraisal?.report_variant;
    const isQuoteOnly = reportVariant === 'quote_only';
    const isFreeEstimate = reportVariant === 'free_estimate';
    const appraisalMode = appraisal?.appraisal_mode || '';
    // iter303ak-fix7 · Corrected field names — RoofAppraisalTab actually
    // saves these keys (verified on A5154 via GET /appraisals/{id}).
    const areaCommentsFilled = AREA_CONDITIONS
      .filter(k => (appraisal?.[k] || '').trim().length > 0).length;
    const pins = appraisal?.roof_diagram_annotations || [];
    const pinsCount = pins.length;
    const countsByType = pins.reduce((acc, p) => { acc[p.type] = (acc[p.type] || 0) + 1; return acc; }, {});
    // iter303ak-fix7 · Read urgent_concerns from the primary roof_details
    // row first (that's where the Runner Action 5 open_property_intake_form
    // saves them via roof_details.urgent_concerns). Property-level is a
    // legacy fallback for older data.
    const urgentConcerns = roofDetails?.urgent_concerns
      || property?.urgent_concerns
      || [];
    const urgentCount = Array.isArray(urgentConcerns) ? urgentConcerns.length : 0;

    const rows = [
      { key: 'screenshot',  ok: hasScreenshot,       label: 'Roof Screenshot',   detail: hasScreenshot ? 'On file ✓' : 'Missing — go back to Action 1' },
      { key: 'template',    ok: hasTemplate,         label: 'Roof Template',     detail: hasTemplate ? 'Selected ✓' : 'Missing — pick a template in Action 1' },
      { key: 'roof_detail', ok: roofFilledCount >= 6, label: 'Roof Details',      detail: `${roofFilledCount}/${ROOF_FIELDS.length} fields filled (${roofPct}%)` },
    ];
    if (!isQuoteOnly && !isFreeEstimate) {
      rows.push(
        { key: 'mode',      ok: !!appraisalMode,     label: 'Appraisal Mode',    detail: appraisalMode ? `Set to ${appraisalMode.replace('_', ' ')}` : 'Not set — Action 3 auto-cascades from report_variant' },
        { key: 'data',      ok: areaCommentsFilled > 0, label: 'Appraisal Data', detail: areaCommentsFilled > 0 ? `${areaCommentsFilled} area comment${areaCommentsFilled === 1 ? '' : 's'} captured` : 'No area comments yet — jump to Action 3 to fill them in' },
        { key: 'pins',      ok: pinsCount > 0,        label: 'Diagram Pins',     detail: pinsCount > 0 ? `${pinsCount} pin${pinsCount === 1 ? '' : 's'} across ${Object.keys(countsByType).length} type${Object.keys(countsByType).length === 1 ? '' : 's'}` : 'No pins — open the annotator in Action 4' },
        { key: 'concerns',  ok: true,                 label: 'Urgent Concerns',  detail: urgentCount > 0 ? `${urgentCount} on file (optional)` : 'None (optional)' },
      );
    }
    return { rows, allCoreOk: rows.filter(r => ['screenshot', 'template', 'roof_detail', 'mode', 'data'].includes(r.key)).every(r => r.ok) };
  })();

  const canContinue = !loading && (checks.allCoreOk || appraisal?.report_variant === 'quote_only');

  return (
    <div className="space-y-4" data-testid="wf-completeness-preview">
      <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4">
        <div className="flex items-start gap-3">
          <ClipboardCheck className="w-5 h-5 text-teal-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-teal-900">Ready to Quote?</div>
            <div className="text-sm text-teal-800 mt-1">
              {params.prompt || 'Final check — everything captured across Actions 1-5 is summarised below. Fix any red X before continuing.'}
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs text-teal-700 hover:text-teal-900 hover:underline inline-flex items-center gap-1"
            data-testid="wf-completeness-refresh"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Reading appraisal data…</div>
      ) : (
        <div className="space-y-2">
          {checks.rows.map(row => {
            const targetOrder = ROW_TO_ACTION_ORDER[row.key];
            const showJump = !row.ok && targetOrder != null && typeof onJumpTo === 'function';
            return (
              <div
                key={row.key}
                className={`flex items-center gap-3 p-3 rounded-lg border ${row.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                data-testid={`wf-completeness-row-${row.key}`}
              >
                {row.ok
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  : <XCircle className="w-5 h-5 text-rose-500 shrink-0" />}
                <div className="flex-1">
                  <div className={`text-sm font-medium ${row.ok ? 'text-emerald-900' : 'text-rose-900'}`}>{row.label}</div>
                  <div className={`text-xs ${row.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{row.detail}</div>
                </div>
                {showJump && (
                  <button
                    type="button"
                    onClick={() => jumpTo(targetOrder)}
                    data-testid={`wf-completeness-jump-${row.key}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-rose-600 text-white rounded hover:bg-rose-700 shrink-0"
                  >
                    Jump to Action {Math.round(targetOrder / 10)} <ArrowRight size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!canContinue && !loading && (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div>Fix the red items above before continuing — use the Jump button on each row to hop straight to the fix.</div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        {canBack && (
          <button onClick={onBack} data-testid="wf-completeness-back"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-completeness-cancel"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={onNext}
          disabled={!canContinue}
          data-testid="wf-completeness-continue"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <CheckCircle2 size={14} /> Continue → ready to build quote
        </button>
      </div>
    </div>
  );
}
